import Box from "@mui/material/Box";
import { useCallback, useEffect, useState } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CardMembershipOutlinedIcon from "@mui/icons-material/CardMembershipOutlined";
import { useSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import { useTranslation } from "react-i18next";
import { Grid, SelectChangeEvent, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import FormDialog from "common/Modal/Modal";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import BackendPaginationTable from "common/Table/TableWithPagination";
import { SubscriptionPlanProfileService } from "subModule/src/core/services";
import { DeleteData, Wrapper } from "subModule/src/type";
import Loader from "layout/Loader";
import { useGetSubscriptionPlanService } from "subModule/src/services/subscriptionplans/useSubscriptionPlan";
import { SubscriptionPlan } from "subModule/src/services/subscriptionplans/interface";
import { useSelector } from "react-redux";
import { selectUserInfo } from "features/user/userSlice";
import SubscriptionPlansSearchHeader from "./SubscriptionPlansSearchHeader";
interface SearchFormData {
  [key: string]: string | any;
}
export default function SubscriptionPlansListing() {
  const navigate = useNavigate();
  const loggedInUserData = useSelector(selectUserInfo);
  const [openDeleteModal, setDeleteModal] = useState(false);
  const [pageNo, setPageNo] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [statusModal, setStatusModal] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [filter, setFilter] = useState<string[]>(["title", "billingCycle"]);
  const [search, setSearch] = useState<any>({});
  const [row, setRow] = useState<SubscriptionPlan>({});
  const [searchFormData, setSearchFormData] = useState<SearchFormData>({
    title: "",
    billingCycle: 0,
  });
  const {
    data: tableData,
    refetch,
    isFetching,
  } = useGetSubscriptionPlanService({
    pageNumber: pageNo,
    size: pageSize,
    ...search,
  });
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const fetchData = useCallback(() => {
    refetch()
      .then((res: any) => {
        if (!res?.data?.pages[0]?.success)
          enqueueSnackbar(
            <AlertMessages
              text={res?.data?.pages[0]?.message || "Something went wrong"}
            />,
            { variant: "error" },
          );
      })
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    fetchData();
  }, [pageNo, pageSize, search]);
  useEffect(() => {
    if (loading) handleSubmitSearch();
  }, [searchFormData.billingCycle]);
  const handleChangePage = (pageNo: number) => {
    setPageNo(pageNo + 1);
  };
  const handleChangeRows = (pageSize: number) => {
    setPageSize(pageSize);
    setPageNo(1);
  };
  const onEdit = (row: any) => navigate(`/subscription-plans/` + row.id);
  const showRowView = (row: any) => {
    navigate(`/subscription-plans/` + row.id);
  };
  const onDelete = (row: any) => {
    setDeleteModal(true);
    setRow(row);
  };
  const clearSearch = () => {
    setFilter(["title", "billingCycle"]);
    setSearchFormData({ title: "", billingCycle: 0 });
    setSearch({});
  };
  const handleChangeFilter = (event: SelectChangeEvent<typeof filter>) => {
    const {
      target: { value },
    } = event;
    setFilter(typeof value === "string" ? value.split(",") : value);
    setSearchFormData((prevFormData) =>
      Object.fromEntries(
        Object.keys(prevFormData).map((key) => [
          key,
          value.includes(key)
            ? prevFormData[key]
            : typeof prevFormData[key] === "string"
              ? ""
              : 0,
        ]),
      ),
    );
    if (!value.length && Object.keys(search).length) {
      clearSearch();
    }
  };
  const handleChangeSearch = (field: string, value: any) => {
    const val = field === "billingCycle" ? value?.value : value;
    if (field === "billingCycle" && searchFormData.billingCycle === val) return;
    setSearchFormData((prevData) => ({
      ...prevData,
      [field]: val,
    }));
    if (field === "billingCycle") setLoading(true);
  };
  const handleSubmitSearch = () => {
    const filteredSearch: Record<string, string | number> = {};
    Object.entries(searchFormData).forEach(([key, value]) => {
      if (value) {
        filteredSearch[key] = typeof value === "string" ? value?.trim() : value;
      }
    });
    setSearch(filteredSearch);
    setPageNo(1);
    setPageSize(pageSize);
  };
  const onChangeStatus = (row: any) => {
    setRow(row);
    setStatusModal(true);
  };
  const actionslist = [
    { label: "edit", onClick: onEdit, icon: <EditIcon /> },
    { label: "delete", onClick: onDelete, icon: <DeleteIcon /> },
  ];
  return (
    <Box>
      <ComponentHeaderTwo
        showBtn={true}
        showSaveBtn={false}
        onClick={() => navigate("/subscription-plans/create")}
        heading={t("subscriptionPlans")}
        icon={
          <CardMembershipOutlinedIcon
            sx={{
              fill: "#000",
              width: "30px",
              height: "30px",
              marginTop: "3px",
            }}
          />
        }
        btnText={"create"}
        btnType={"create"}
        showCreateBtn={
          loggedInUserData?.type === -1 || loggedInUserData?.type === 100
        }
      />
      <Box>
        {isFetching || loading ? (
          <Loader />
        ) : (
          <BackendPaginationTable
            columns={[
              { id: "name", label: "name", numeric: false, width: "35%" },
              { id: "price", label: "price", numeric: true, width: "18%" },
              {
                id: "billingCycle",
                label: "billingCycle",
                numeric: false,
                width: "22%",
              },
              ...(loggedInUserData?.type === -1 ||
              loggedInUserData?.type === 100
                ? [
                    {
                      id: "active",
                      label: "status",
                      numeric: false,
                      width: "14%",
                    },
                  ]
                : []),
              ...(loggedInUserData?.type === -1 ||
              loggedInUserData?.type === 100
                ? [
                    {
                      id: "actions",
                      label: "actions",
                      numeric: false,
                      width: "8%",
                    },
                  ]
                : []),
            ]}
            data={tableData?.pages[0]?.data?.content || []}
            pageNo={pageNo}
            pageSize={pageSize}
            tableData={tableData?.pages[0]?.data || {}}
            showCheckbox={false}
            showActions={true}
            enableSorting={true}
            showHeader={true}
            showSearch={true}
            showFilter={true}
            showCreateButton={true}
            actions={
              loggedInUserData?.type === -1 || loggedInUserData?.type === 100
                ? actionslist
                : []
            }
            handleChangePage={handleChangePage}
            handleChangeRows={handleChangeRows}
            onChangeStatus={
              loggedInUserData?.type === -1 || loggedInUserData?.type === 100
                ? onChangeStatus
                : undefined
            }
            rowClick={loggedInUserData?.type === -1}
            showRowView={showRowView}
            loadingSearch={false}
            headerComponent={
              <>
                <SubscriptionPlansSearchHeader
                  searchFormData={searchFormData}
                  handleChangeFilter={handleChangeFilter}
                  handleChangeSearch={handleChangeSearch}
                  handleSubmitSearch={handleSubmitSearch}
                  clearSearch={clearSearch}
                  {...{ filter, search }}
                />
              </>
            }
          />
        )}
      </Box>
      {openDeleteModal && (
        <FormDialog
          open={openDeleteModal}
          onClose={() => setDeleteModal(false)}
          title={t("delete") + " " + t("subscriptionPlan")}
          content={
            <Grid container>
              <Grid item xs={12}>
                <Typography>{t("Are you sure you want to delete?")}</Typography>
              </Grid>
            </Grid>
          }
          saveFunction={() => {
            setDeleteModal(false);
            setLoading(true);
            SubscriptionPlanProfileService.deleteSubscriptionPlan(
              (row as any).id,
            )
              .then((response: DeleteData) => {
                if (response.success) {
                  if (
                    tableData?.pages[0]?.data?.content?.length === 1 &&
                    pageNo > 1
                  )
                    setPageNo(1);
                  else refetch();
                  enqueueSnackbar(
                    <AlertMessages
                      statusCode={response.status}
                      text={
                        t("subscriptionPlan") +
                        " " +
                        t("deleted") +
                        " " +
                        t("successfully")
                      }
                    />,
                    { variant: "success" },
                  );
                } else {
                  enqueueSnackbar(<AlertMessages text={response.message} />, {
                    variant: "error",
                  });
                }
              })
              .catch((err: any) => {
                console.error("===", err);
                enqueueSnackbar(
                  <AlertMessages
                    text={err?.data?.message || err || "Something went wrong"}
                  />,
                  { variant: "error" },
                );
              })
              .finally(() => setLoading(false));
          }}
          submitButtonText="Delete"
        />
      )}
      {statusModal && (
        <FormDialog
          open={statusModal}
          onClose={() => setStatusModal(false)}
          title="Change Status"
          content={
            <Grid container>
              <Grid item xs={12}>
                <Typography>{t(`Are you sure?`)}</Typography>
              </Grid>
            </Grid>
          }
          saveFunction={() => {
            setStatusModal(false);
            setLoading(true);
            SubscriptionPlanProfileService.changeStatusSubscriptionPlan(
              (row as any).id,
              row.active ? "inactive" : "active",
              {},
            )
              .then((response: Wrapper) => {
                if (response.success) {
                  refetch();
                  enqueueSnackbar(
                    <AlertMessages
                      statusCode={response.status}
                      text={
                        t("status") +
                        " " +
                        t("changed") +
                        " " +
                        t("successfully")
                      }
                    />,
                    { variant: "success" },
                  );
                } else {
                  enqueueSnackbar(
                    <AlertMessages
                      text={response?.message || "Something went wrong"}
                    />,
                    {
                      variant: "error",
                    },
                  );
                }
              })
              .catch((err: any) => {
                enqueueSnackbar(
                  <AlertMessages
                    text={err?.data?.message || err || "Something went wrong"}
                  />,
                  { variant: "error" },
                );
              })
              .finally(() => setLoading(false));
          }}
          submitButtonText="OK"
        />
      )}
    </Box>
  );
}
